import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type ClientStatus, type Company, type HistoryEntry, type Me } from "../lib/api";
import { CompanyFacts, StatusChip } from "./Companies";
import { CompanyCrm } from "./CompanyCrm";
import { TermsSection } from "./TermsSection";
import { CompanyJobs } from "./CompanyJobs";
import { useTitle } from "../lib/title";

const pad = (n: number) => String(n).padStart(2, "0");
const when = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const KIND_LABEL: Record<HistoryEntry["kind"], string> = {
  create: "Added",
  update: "Edited",
  revert: "Reverted",
  lock: "Locked",
  unlock: "Unlocked",
};

export function CompanyPage({ me }: { me: Me }) {
  const { id = "" } = useParams();
  const [company, setCompany] = useState<Company | null>(null);
  useTitle(company?.name ?? "Company");
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const load = useAction();

  const reload = useCallback(
    () =>
      load.run(async () => {
        const [{ company }, { history }] = await Promise.all([api.company(id), api.companyHistory(id)]);
        setCompany(company);
        setHistory(history);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );
  useEffect(() => void reload(), [reload]);

  if (!company) {
    return (
      <>
        <p className="label">
          <Link to="/companies">← Companies</Link>
        </p>
        <ErrorNotice error={load.error} />
        {!load.error && <p className="label">Loading…</p>}
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <p className="label">
          <Link to="/companies">← Companies</Link>
          {company.locked && <span> · Locked profile</span>}
        </p>
        <h1 className="display">{company.name}</h1>
        <CompanyFacts company={company} />
      </div>

      <Relationship company={company} me={me} onChange={setCompany} />
      {company.client && (
        <CompanyCrm company={company}>
          <CompanyJobs company={company} />
          <TermsSection company={company} onMarkedClient={setCompany} />
        </CompanyCrm>
      )}
      <Profile company={company} onChange={() => void reload()} />
      <History company={company} history={history} onChange={() => void reload()} />
    </>
  );
}

/**
 * This agency's private record: lead or client. Notes now go in the activity log, which keeps each
 * entry with its time and author; notes written before that are still shown, read-only.
 */
function Relationship({ company, me, onChange }: { company: Company; me: Me; onChange: (c: Company) => void }) {
  const { pending, error, run } = useAction();

  const save = (change: { status?: ClientStatus }) =>
    void run(async () => {
      onChange((await api.saveClient(company.id, change)).company);
    });

  const remove = () =>
    void run(async () => {
      await api.removeClient(company.id);
      onChange({ ...company, client: null });
    });

  return (
    <section className="section" aria-labelledby="rel-h">
      <div className="section-head">
        <h2 id="rel-h" className="label">
          Your relationship
        </h2>
        <span className="label">Private to {me.tenant.name}</span>
      </div>
      <div className="form narrow">
        <ErrorNotice error={error} />
        {company.client ? (
          <>
            <fieldset className="segmented">
              <legend className="label">Status</legend>
              {(["lead", "client"] as const).map((s) => (
                <label key={s}>
                  <input
                    type="radio"
                    name="client-status"
                    checked={company.client?.status === s}
                    disabled={pending}
                    onChange={() => save({ status: s })}
                  />
                  <span>{s === "lead" ? "Lead" : "Client"}</span>
                </label>
              ))}
            </fieldset>
            {company.client.notes.trim() && (
              <div className="field">
                <span className="label">Earlier notes</span>
                <p className="prewrap">{company.client.notes}</p>
                <span className="hint">From before the activity log. Log new notes under Activity, where each keeps its time and author.</span>
              </div>
            )}
            <div className="button-row">
              <button type="button" className="link-btn danger" disabled={pending} onClick={remove}>
                Remove from your list
              </button>
            </div>
          </>
        ) : (
          <>
            <p>This company isn't on your list.</p>
            <div className="button-row">
              <button className="btn" disabled={pending} onClick={() => save({ status: "lead" })}>
                Add as lead
              </button>
              <button className="btn secondary" disabled={pending} onClick={() => save({ status: "client" })}>
                Add as client
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/** The shared profile every agency sees. */
function Profile({ company, onChange }: { company: Company; onChange: () => void }) {
  const { pending, error, run } = useAction();
  const [fields, setFields] = useState({
    name: company.name,
    website: company.website ?? "",
    location: company.location ?? "",
    industry: company.industry ?? "",
  });
  useEffect(
    () =>
      setFields({
        name: company.name,
        website: company.website ?? "",
        location: company.location ?? "",
        industry: company.industry ?? "",
      }),
    [company],
  );
  const dirty =
    fields.name !== company.name ||
    fields.website !== (company.website ?? "") ||
    fields.location !== (company.location ?? "") ||
    fields.industry !== (company.industry ?? "");

  const set = (key: keyof typeof fields) => (e: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [key]: e.target.value }));

  const save = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.updateCompany(company.id, {
        name: fields.name.trim(),
        website: fields.website.trim() || null,
        location: fields.location.trim() || null,
        industry: fields.industry.trim() || null,
      });
      onChange();
    });
  };

  const toggleLock = () =>
    void run(async () => {
      await api.updateCompany(company.id, { locked: !company.locked });
      onChange();
    });

  return (
    <section className="section" aria-labelledby="profile-h">
      <div className="section-head">
        <h2 id="profile-h" className="label">
          Shared profile
        </h2>
        <span className={`status ${company.locked ? "" : "off"}`}>{company.locked ? "Locked" : "Open to edits"}</span>
      </div>
      <form className="form narrow" onSubmit={save}>
        {!company.canEdit && (
          <p className="notice">Locked by the agency that added this company. You can still keep your own notes above.</p>
        )}
        <fieldset className="grid-2 plain" disabled={!company.canEdit || pending}>
          <Field label="Company name" required maxLength={200} value={fields.name} onChange={set("name")} />
          <Field label="Website" inputMode="url" value={fields.website} onChange={set("website")} />
          <Field label="Location" value={fields.location} onChange={set("location")} />
          <Field label="Industry" value={fields.industry} onChange={set("industry")} />
        </fieldset>
        <ErrorNotice error={error} />
        <div className="button-row">
          {company.canEdit && (
            <button className="btn secondary" disabled={pending || !dirty}>
              Save profile
            </button>
          )}
          {company.canLock && (
            <button type="button" className="link-btn" disabled={pending} onClick={toggleLock}>
              {company.locked ? "Unlock for everyone" : "Lock to your agency"}
            </button>
          )}
        </div>
        {company.canLock && (
          <p className="hint">
            {company.locked
              ? "Only you and your company's owners can edit this profile."
              : "Any agency can correct this profile. Lock it if you'd rather only your team made changes."}
          </p>
        )}
      </form>
    </section>
  );
}

function History({ company, history, onChange }: { company: Company; history: HistoryEntry[]; onChange: () => void }) {
  const { pending, error, run } = useAction();
  const latest = history[0]?.revision;
  const revert = (h: HistoryEntry) =>
    void run(async () => {
      await api.revertCompany(company.id, h.revision);
      onChange();
    });

  return (
    <section className="section" aria-labelledby="history-h">
      <div className="section-head">
        <h2 id="history-h" className="label">
          History
        </h2>
        <span className="label figures">{pad(history.length)} changes</span>
      </div>
      <ErrorNotice error={error} />
      <ol className="index">
        {history.map((h) => (
          <li key={h.revision}>
            <span className="num" aria-hidden>
              {pad(h.revision)}
            </span>
            <span className="main">
              <span className="primary">
                {KIND_LABEL[h.kind]}{" "}
                <span className="secondary">
                  by {h.byYourTeam ? (h.changedBy ?? "your team") : "another agency"} ·{" "}
                  <span className="figures">{when.format(new Date(h.createdAt))}</span>
                </span>
              </span>
              <span className="secondary">
                {[h.data.name, h.data.website, h.data.location, h.data.industry].filter(Boolean).join(" · ")}
              </span>
            </span>
            <span className="actions">
              {company.canEdit && h.revision !== latest && h.kind !== "lock" && h.kind !== "unlock" && (
                <button type="button" className="link-btn" disabled={pending} onClick={() => revert(h)}>
                  Restore this version
                </button>
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
